import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUIStore } from '../../stores/uiStore';
import { useSidecarMode } from '../../context/SidecarModeContext';
import { useAuth } from '../../context/AuthContext';
import { chemistryApi } from '../../api/chemistry';
import { ChemFile } from '../../types/chemistry';
import { useFeatureStore } from '../../stores/featureStore';
import '../../styles/Sidebar.css';

interface SidebarItem {
  id: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  variant?: 'upload';
  hiddenInSidecar?: boolean;
}

const DashboardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
  </svg>
);

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const FileTextIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <line x1="10" y1="9" x2="8" y2="9" />
  </svg>
);

const CrosshairIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="22" y1="12" x2="18" y2="12" />
    <line x1="6" y1="12" x2="2" y2="12" />
    <line x1="12" y1="6" x2="12" y2="2" />
    <line x1="12" y1="22" x2="12" y2="18" />
  </svg>
);

const ReactionIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12h16" />
    <polyline points="14 6 20 12 14 18" />
    <circle cx="7" cy="12" r="1" fill="currentColor" />
  </svg>
);

const ProteinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 7c0 0 3-3 8-3s8 3 8 3" />
    <path d="M4 12c0 0 3 3 8 3s8-3 8-3" />
    <path d="M4 17c0 0 3-3 8-3s8 3 8 3" />
  </svg>
);

const FolderIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);


const KnowledgeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </svg>
);

const LibraryIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    <line x1="12" y1="6" x2="12" y2="13" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const SharedIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const InventoryIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

const RegistryIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    <path d="M9 14l2 2 4-4" />
  </svg>
);

const PlatesIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8" cy="8" r="1.5" fill="currentColor" />
    <circle cx="12" cy="8" r="1.5" fill="currentColor" />
    <circle cx="16" cy="8" r="1.5" fill="currentColor" />
    <circle cx="8" cy="12" r="1.5" fill="currentColor" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    <circle cx="16" cy="12" r="1.5" fill="currentColor" />
    <circle cx="8" cy="16" r="1.5" fill="currentColor" />
    <circle cx="12" cy="16" r="1.5" fill="currentColor" />
    <circle cx="16" cy="16" r="1.5" fill="currentColor" />
  </svg>
);

const BatchesIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 3H3.6a.6.6 0 0 0-.6.6v16.8a.6.6 0 0 0 .6.6H9m0-18h12.4a.6.6 0 0 1 .6.6v16.8a.6.6 0 0 1-.6.6H9m0-18v18" />
    <path d="M13 8h5" />
    <path d="M13 12h5" />
    <path d="M13 16h3" />
  </svg>
);

const AssayQueueIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 3h6" />
    <path d="M10 3v4.4a1 1 0 0 1-.2.6L5.3 14.8a1 1 0 0 0-.2.6V19a2 2 0 0 0 2 2h9.4a2 2 0 0 0 2-2v-3.6a1 1 0 0 0-.2-.6L13.8 8a1 1 0 0 1-.2-.6V3" />
    <path d="M8.5 14h7" />
  </svg>
);

const SpectraIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3v18h18" />
    <path d="M7 16l4-8 3 5 4-9" />
  </svg>
);

const SettingsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const CHEM_EXT = new Set(['sdf','mol','mol2','smi','smiles','csv','xlsx','xls','cdx','cdxml','cml','inchi','rxn','rdf']);
const isChemFile = (f: ChemFile) => {
  if (f.fileType) return f.fileType === 'molecules';
  // No fileType set — fall back to extension check (backward compat for older files)
  // Check originalFileName first, then fileName (originalFileName may lack extension after rename)
  const origExt = (f.originalFileName || '').split('.').pop()?.toLowerCase();
  if (origExt && CHEM_EXT.has(origExt)) return true;
  const ext = (f.fileName || '').split('.').pop()?.toLowerCase();
  return !!ext && CHEM_EXT.has(ext);
};
const cleanFileName = (name: string) => name.replace(/^[a-f0-9]{8}_/i, '');
const getAbbrev = (name: string) => {
  const clean = cleanFileName(name).replace(/\.[^.]+$/, '');
  return clean.slice(0, 3).toUpperCase();
};

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { sidecarMode } = useSidecarMode();
  const { isGuest } = useAuth();

  // ─── Slim file icons: detect project context ────────────
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isCompoundsView = location.pathname === '/chemistry/files'
    && searchParams.has('project')
    && (!searchParams.has('tab') || searchParams.get('tab') === 'compounds');
  const isEditorView = location.pathname.startsWith('/chemistry/file/') && location.pathname.endsWith('/editor');
  const showFileIcons = isCompoundsView || isEditorView;
  const projectIdFromUrl = isCompoundsView ? searchParams.get('project') : null;
  const activeFileId = searchParams.get('file') || null;

  const [sidebarFiles, setSidebarFiles] = useState<ChemFile[]>([]);

  useEffect(() => {
    if (!showFileIcons || !projectIdFromUrl) {
      setSidebarFiles([]);
      return;
    }
    let cancelled = false;
    chemistryApi.getFiles(Number(projectIdFromUrl))
      .then(files => { if (!cancelled) setSidebarFiles(files.filter(isChemFile)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showFileIcons, projectIdFromUrl]);

  const mainItems: SidebarItem[] = [
    { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon />, hiddenInSidecar: true },
    { id: 'upload', label: 'Upload', path: '#upload', icon: <UploadIcon />, variant: 'upload', hiddenInSidecar: true },
    { id: 'projects', label: 'Projects', path: '/chemistry', icon: <FolderIcon />, hiddenInSidecar: true },
    { id: 'files', label: 'All Files', path: '/files', icon: <FileTextIcon />, hiddenInSidecar: true },
    { id: 'shared', label: 'Shared with Me', path: '/chemistry/files?view=shared', icon: <SharedIcon />, hiddenInSidecar: true },
    { id: 'library', label: 'Reference Library', path: '/chemistry/files?view=library', icon: <LibraryIcon />, hiddenInSidecar: true },
    { id: 'registry', label: 'Registry', path: '/chemistry/registry', icon: <RegistryIcon />, hiddenInSidecar: true },
    { id: 'inventory', label: 'Company Inventory', path: '/inventory', icon: <InventoryIcon />, hiddenInSidecar: true },
    { id: 'reactions', label: 'Reactions', path: '/chemistry/reactions', icon: <ReactionIcon />, hiddenInSidecar: true },
    { id: 'targets', label: 'Targets', path: '/omics', icon: <CrosshairIcon />, hiddenInSidecar: true },
    { id: 'batches', label: 'Batches', path: '/batches', icon: <BatchesIcon />, hiddenInSidecar: true },
    { id: 'plates', label: 'Assays', path: '/biology/assays', icon: <PlatesIcon />, hiddenInSidecar: true },
    { id: 'assay-queue', label: 'Assay Queue', path: '/biology/assay-queue', icon: <AssayQueueIcon />, hiddenInSidecar: true },
    { id: 'search', label: 'Quick Search', path: '/chemistry/quick-search', icon: <SearchIcon />, hiddenInSidecar: true },
    // Spectra nav item removed — route kept for direct access
  ];

  const { isModuleEnabled } = useFeatureStore();

  const sidebarModGate: Record<string, string> = {
    registry: 'mod_registry',
    targets: 'mod_targets',
    batches: 'mod_batches',
    plates: 'mod_assays',
    'assay-queue': 'mod_assays',
  };

  const visibleItems = mainItems.filter((item) => {
    const key = sidebarModGate[item.id];
    return !key || isModuleEnabled(key);
  });

  const bottomItems: SidebarItem[] = [
    { id: 'settings', label: 'Admin', path: '/admin', icon: <SettingsIcon />, hiddenInSidecar: true },
  ];

  const isActive = (item: SidebarItem) => {
    if (item.id === 'upload') return false;
    if (item.id === 'files') {
      return location.pathname === '/files';
    }
    if (item.id === 'shared') {
      return location.pathname === '/chemistry/files' && new URLSearchParams(location.search).get('view') === 'shared';
    }
    if (item.id === 'library') {
      return location.pathname === '/chemistry/files' && new URLSearchParams(location.search).get('view') === 'library';
    }
    if (item.id === 'dashboard') return location.pathname === '/dashboard';
    if (item.id === 'projects') return location.pathname === '/chemistry' || location.pathname === '/chemistry/projects';
    if (item.id === 'registry') return location.pathname === '/chemistry/registry';
    if (item.id === 'inventory') return location.pathname.startsWith('/inventory');
    if (item.id === 'reactions') return location.pathname.startsWith('/chemistry/reactions');
    if (item.id === 'search') return location.pathname === '/chemistry/quick-search';
    if (item.id === 'batches') return location.pathname.startsWith('/batches');
    if (item.id === 'assay-queue') return location.pathname === '/biology/assay-queue';
    if (item.id === 'plates') return location.pathname.startsWith('/biology') && location.pathname !== '/biology/assay-queue';
    if (item.id === 'spectra') return location.pathname === '/spectra';
    return location.pathname === item.path || location.pathname.startsWith(item.path + '/');
  };

  const openUploadDialog = useUIStore((state) => state.openUploadDialog);

  const handleClick = (item: SidebarItem, e: React.MouseEvent) => {
    if (item.id === 'upload') {
      e.preventDefault();
      if (isGuest) return; // Blocked — GuestGate handles the tooltip
      // Contextual upload: if inside a project, navigate to the Files tab
      const projectMatch = location.search.match(/[?&]project=(\d+)/);
      if (projectMatch || location.pathname.startsWith('/chemistry/file')) {
        // Extract project ID from URL or navigate to files
        if (projectMatch) {
          navigate(`/chemistry/files?project=${projectMatch[1]}&tab=documents`);
        } else {
          openUploadDialog();
        }
      } else {
        openUploadDialog();
      }
    }
  };

  const renderItem = (item: SidebarItem, index: number) => {
    const active = isActive(item);
    const shouldHide = sidecarMode && item.hiddenInSidecar;
    const isGuestBlocked = isGuest && item.variant === 'upload';

    const className = [
      'sidebar__item',
      active ? 'sidebar__item--active' : '',
      item.variant === 'upload' ? 'sidebar__item--upload' : '',
      shouldHide ? 'sidebar__item--hidden' : '',
      isGuestBlocked ? 'sidebar__item--disabled' : '',
    ].filter(Boolean).join(' ');

    const tooltipText = isGuestBlocked
      ? 'Upload your own molecules. Start a trial to get started.'
      : item.label;

    return (
      <div
        key={item.id}
        className={`sidebar__item-wrapper ${shouldHide ? 'sidebar__item-wrapper--hidden' : ''}`}
        style={{ transitionDelay: `${index * 20}ms` }}
      >
        <Link
          to={item.path}
          className={className}
          onClick={(e) => handleClick(item, e)}
        >
          <span className="sidebar__icon">{item.icon}</span>
          <span className="sidebar__tooltip">{tooltipText}</span>
        </Link>
      </div>
    );
  };

  return (
    <aside className={`sidebar ${sidecarMode ? 'sidebar--sidecar' : ''}`}>
      <div className="sidebar__logo">
        <Link to="/dashboard" className="sidebar__logo-mark" title="Adroit AI — Adroit">
          {/* Chaos-to-order mark: 5 vertical bars, fragmented → solid */}
          <svg viewBox="0 0 40 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="2" width="4" height="5" rx="1" fill="currentColor" opacity="0.3" />
            <rect x="2" y="10" width="4" height="4" rx="1" fill="currentColor" opacity="0.35" />
            <rect x="2" y="18" width="4" height="3" rx="1" fill="currentColor" opacity="0.25" />
            <rect x="2" y="25" width="4" height="5" rx="1" fill="currentColor" opacity="0.4" />
            <rect x="10" y="3" width="4" height="7" rx="1" fill="currentColor" opacity="0.5" />
            <rect x="10" y="14" width="4" height="5" rx="1" fill="currentColor" opacity="0.5" />
            <rect x="10" y="23" width="4" height="7" rx="1" fill="currentColor" opacity="0.55" />
            <rect x="18" y="2" width="4" height="10" rx="1" fill="currentColor" opacity="0.65" />
            <rect x="18" y="16" width="4" height="14" rx="1" fill="currentColor" opacity="0.7" />
            <rect x="26" y="1" width="4" height="14" rx="1" fill="currentColor" opacity="0.85" />
            <rect x="26" y="18" width="4" height="13" rx="1" fill="currentColor" opacity="0.9" />
            <rect x="34" y="1" width="4" height="30" rx="1" fill="currentColor" opacity="1" />
          </svg>
        </Link>
      </div>

      <nav className="sidebar__nav">
        {visibleItems.map((item, index) => renderItem(item, index))}
      </nav>

      {/* Slim file icons — visible when viewing compounds */}
      {showFileIcons && sidebarFiles.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '4px 8px', width: '100%' }}>
          <div style={{ width: 28, height: 1, background: 'var(--sidebar-border)', marginBottom: 4 }} />
          {sidebarFiles.slice(0, 12).map(file => {
            const name = cleanFileName(file.originalFileName || file.fileName);
            const abbrev = getAbbrev(file.originalFileName || file.fileName);
            const isActive = file.id === activeFileId;
            const params = new URLSearchParams(location.search);
            params.set('file', file.id);
            params.delete('view');
            const filePath = `/chemistry/files?${params.toString()}`;
            return (
              <Link
                key={file.id}
                to={filePath}
                title={`${name}\n${file.moleculesLibraryCount} molecules`}
                style={{
                  width: 36,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 6,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  transition: 'background 0.15s, color 0.15s',
                  background: isActive ? 'var(--sidebar-active-bg)' : 'transparent',
                  color: isActive ? 'var(--sidebar-icon-active)' : 'var(--sidebar-icon-color)',
                }}
                onMouseEnter={e => { if (!isActive) (e.target as HTMLElement).style.background = 'var(--sidebar-active-bg)'; }}
                onMouseLeave={e => { if (!isActive) (e.target as HTMLElement).style.background = 'transparent'; }}
              >
                {abbrev}
              </Link>
            );
          })}
          <div style={{ width: 28, height: 1, background: 'var(--sidebar-border)', marginTop: 4 }} />
        </div>
      )}

      <div className="sidebar__divider" />

      <div className="sidebar__bottom">
        {bottomItems.map((item, index) => renderItem(item, mainItems.length + index))}
      </div>

      {/* Privacy footer link */}
      <Link
        to="/privacy"
        className="sidebar__privacy-link"
        title="Privacy & Data"
      >
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 12, height: 12 }}>
          <path d="M8 1L2 4v4c0 3.5 2.5 6.5 6 7.5 3.5-1 6-4 6-7.5V4L8 1z" />
        </svg>
      </Link>
    </aside>
  );
};

export default Sidebar;
